import { Hysplit, HysplitOptions, HysplitHeightSVG, HysplitLatLngSVG } from './hysplit';
import { TemporalDataCoordinate } from '../data/item';
import { Map as TLMDMap } from '../map//map';
import { Timeline, OverlayType } from '../timeline/timeline';
import { TimelineRange } from '../timeline/timeline-common';
import { OverlayView as TimelineOverlayView, TimelineOverlayCategory } from '../timeline/timeline-overlay';
import { MapOverlay, MapOverlayCategory } from '../map/map-overlay';
import { MapOverlaySVGContentProducer } from '../map/svg-overlay';
import { SpatialDataCoordinate } from '../data-source/types';

export interface HysplitViewerOptions {
	timelineOverlayHeight?: number,
	bringInView?: { map?: boolean, timeline?: boolean }
}

export class HysplitViewer {
	private timelineOverlay: HysplitTimelineOverlay;
	private mapOverlay: HysplitMapOverlay;
	private map: TLMDMap;
	private timeline: Timeline;
	private hysplit: Hysplit
	private options = {
		timelineOverlayHeight: 100,
		bringInView: {
			map: false,
			timeline: false
		}
	}
	constructor(options?: HysplitViewerOptions) {
		if (options) {
			this.options.timelineOverlayHeight = options.timelineOverlayHeight === undefined || options.timelineOverlayHeight <= 0 ? this.options.timelineOverlayHeight : options.timelineOverlayHeight;
			if (options.bringInView) {
				this.options.bringInView.map = options.bringInView.map === undefined ? this.options.bringInView.map : options.bringInView.map;
				this.options.bringInView.timeline = options.bringInView.timeline === undefined ? this.options.bringInView.timeline : options.bringInView.timeline;
			}
		}
	}
	public getHysplit(): Hysplit {
		return this.hysplit;
	}
	public setHysplit(hysplit: Hysplit): HysplitViewer {
		this.hysplit = hysplit;
		if (hysplit) {
			if (this.mapOverlay) {
				this.mapOverlay.setHysplit(hysplit);
			} else {
				if (this.hysplit && this.map) {
					this.mapOverlay = new HysplitMapOverlay(this.hysplit);
					this.mapOverlay.setMap(this.map);
				}
			}
			if (this.timelineOverlay) {
				this.timelineOverlay.setHysplit(hysplit);
			} else {
				if (this.hysplit && this.timeline) {
					this.timelineOverlay = new HysplitTimelineOverlay(this.options.timelineOverlayHeight, (timeRange: TimelineRange) => {
						if (this.mapOverlay) {
							this.mapOverlay.updateSVG(timeRange.cursor ? timeRange.cursor.center : undefined);
						}
					}, this.timeline, this.hysplit);
				}
			}
			if (this.options.bringInView && this.options.bringInView.timeline === true) {
				this.bringInTimelineView();
			}
			if (this.options.bringInView && this.options.bringInView.map === true) {
				this.bringInMapView();
			}
		} else {
			if (this.mapOverlay) {
				this.mapOverlay.setHysplit(undefined);
			}
			if (this.timelineOverlay) {
				this.timelineOverlay.setHysplit(undefined);
			}
		}
		return this;
	}
	public getMap(): TLMDMap {
		return this.map;
	}
	public setMap(map: TLMDMap): HysplitViewer {
		this.map = map;
		if (map) {
			if (this.mapOverlay) {
				this.mapOverlay.setMap(null);
				this.mapOverlay.setMap(map);
			} else {
				if (this.hysplit) {
					this.mapOverlay = new HysplitMapOverlay(this.hysplit);
					this.mapOverlay.setMap(this.map);
				}
			}
			if (this.options.bringInView && this.options.bringInView.map === true) {
				this.bringInMapView();
			}
		} else {
			if (this.mapOverlay) {
				this.mapOverlay.setMap(null);
			}
		}
		return this;
	}
	public getTimeline(): Timeline {
		return this.timeline;
	}
	public setTimeline(timeline: Timeline): HysplitViewer {
		this.timeline = timeline;
		if (timeline) {
			if (this.timelineOverlay) {
				this.timelineOverlay.setTimeline(null);
				this.timelineOverlay.setTimeline(timeline);
			} else {
				if (this.hysplit) {
					this.timelineOverlay = new HysplitTimelineOverlay(this.options.timelineOverlayHeight, (timeRange: TimelineRange) => {
						if (this.mapOverlay) {
							this.mapOverlay.updateSVG(timeRange.cursor ? timeRange.cursor.center : undefined);
						}
					}, this.timeline, this.hysplit);
				}
			}
			if (this.options.bringInView && this.options.bringInView.timeline === true) {
				this.bringInTimelineView();
			}
		} else {
			if (this.timelineOverlay) {
				this.timelineOverlay.setTimeline(null);
			}

		}
		return this;
	}
	public setOptions(options: HysplitOptions): HysplitViewer {
		if (this.hysplit) {
			this.hysplit.setOptions(options);
		}
		if (this.timelineOverlay) {
			this.timelineOverlay.refresh();
		}
		if (this.mapOverlay) {
			this.mapOverlay.refresh();
		}
		return this;
	}
	public refresh(): HysplitViewer {
		if (this.hysplit) {
			this.hysplit.setOptions(this.hysplit.options);
		}
		if (this.timelineOverlay) {
			this.timelineOverlay.refresh();
		}
		if (this.mapOverlay) {
			this.mapOverlay.refresh();
		}
		return this;
	}
	public bringInTimelineView(): HysplitViewer {
		if (this.timelineOverlay) {
			this.timelineOverlay.inView();
		}
		return this;
	}
	public bringInMapView(): HysplitViewer {
		if (this.mapOverlay) {
			this.mapOverlay.inView();
		}
		return this;
	}
	public getTemporalCoordinate(): TemporalDataCoordinate {
		if (this.timelineOverlay) {
			return this.timelineOverlay.getTemporalCoordinate();
		}
		return undefined;
	}
	public getSpatialCoordinate(): SpatialDataCoordinate {
		if (this.mapOverlay) {
			//            return this.mapOverlay.getSpatialCoordinate();
			return this.mapOverlay.getSpatialCoordinate();
		}
		return undefined;
	}
}
class HysplitTimelineOverlay extends TimelineOverlayView {
	private svg: HysplitHeightSVG;
	private onUpdateTimer: number;

	constructor(private height: number, private onUpdate: (timeRange: TimelineRange) => void, timeline?: Timeline, private hysplit?: Hysplit) {
		super(TimelineOverlayCategory.OBSERVATION_TRAJECT);
		this.height = height;
		this.onUpdate = onUpdate;
		this.hysplit = hysplit;

		this.svg = null;
		this.onUpdateTimer = null;
		this.setTimeline(timeline);
	}
	public setHysplit(hysplit: Hysplit): HysplitTimelineOverlay {
		this.hysplit = hysplit;
		this.svg = undefined;
		this.draw();
		return this;
	}
	private updateSVG(mspp: number, tracerMillis: number) {
		let layer = this.getLayer();
		if (this.hysplit && layer) {
			this.svg = this.hysplit.getHeightSVG(mspp, this.getHeight(), tracerMillis);
			$(layer).html(this.svg.markup);
			let svgElem: SVGElement = <SVGElement>layer.firstChild;
			svgElem.style.width = '100%';
			svgElem.style.height = '100%';
			svgElem.style.position = 'absolute';
		}
	}
	public inView(): HysplitTimelineOverlay {
		if (this.getTimeline() && this.svg) {
			this.getTimeline().setRange(this.svg.time.start, this.svg.time.end);
		}
		return this;
	}
	public getTemporalCoordinate(): TemporalDataCoordinate {
		if (this.getTimeline() && this.svg) {
			return {
				time: undefined,
				start: this.svg.time.start,
				end: this.svg.time.end,
				duration: this.svg.time.end - this.svg.time.start,
				perpetual: false,
				moment: false,
				since: false,
				until: false
			}
		}
		return undefined;
	}
	public refresh(): HysplitTimelineOverlay {
		this.svg = null;
		this.draw();
		return this;
	}
	public getType(): number {
		return OverlayType.Static + OverlayType.Animated;
	}
	protected getHeight(): number {
		return this.height;
	}
	protected onAdd(): void {
		super.onAdd();
		this.svg = null;
	}
	protected draw(): void {
		if (this.hysplit && this.getTimeline()) {
			let tr: TimelineRange = this.getTimeline().getTimeRange(true); //cache this one for speed

			if (!this.svg || !this.svg.isInSync(tr.mspp, tr.cursor ? tr.cursor.center : undefined)) {
				//we are in need of (new) svg
				this.updateSVG(tr.mspp, tr.cursor ? tr.cursor.center : undefined);
			}

			let svgElem: SVGElement = <SVGElement>this.getLayer().firstChild;

			let newViewBoxAttrib = this.svg.getViewBoxAttribute(this.getTimeline().getWidth(), tr.mspp, tr.visible.start);
			let currentViewboxAttrib = svgElem.getAttribute("viewBox");

			if (newViewBoxAttrib !== currentViewboxAttrib) {
				//adjust the viewBox
				svgElem.setAttribute("viewBox", newViewBoxAttrib);
			}
			//give the timeline a chance to do other work first
			if (this.onUpdateTimer != null) {
				clearTimeout(this.onUpdateTimer);
				this.onUpdateTimer = null;
			}
			if (this.onUpdate) {
				this.onUpdateTimer = window.setTimeout(() => this.onUpdate(tr), 1);
			}
		}
	}
	protected onRemove(): void {
		super.onRemove();
		this.svg = null;
	};
}

class HysplitMapOverlay extends MapOverlay {

	constructor(private hysplit?: Hysplit) {
		super(
			MapOverlayCategory.OBSERVATION_TRAJECT,
			'',
			{
				elementData: {
				}
			},
			new MapOverlaySVGContentProducer((svgElem, svg) => {
				//adjust stroke widths depending on the zoom level
				let ratio = parseFloat(svgElem.style.width) / svg.viewBox.width;

				var baseWidth = (1 / ratio);

				let mainWidth = baseWidth * 2 + 0.6 * ratio / 128;
				let contourWidth = baseWidth + 0.2 * ratio / 128;
				let trajWidth = baseWidth * 1.0 + 0.1 * ratio / 128;

				$('g.contour path', svgElem).css({
					'stroke-width': contourWidth + 'px'
				});
				$('g.trajectory path', svgElem).css({
					'stroke-width': trajWidth + 'px'
				});
				$('g.trajectory circle', svgElem).css({
					'stroke-width': trajWidth + 'px'
				}).attr("r", (trajWidth * 3).toString());
				//            $('g.trajectory.main path, g.trajectory.analyzed path', this.div).css({
				$('g.trajectory.main path', svgElem).css({
					'stroke-width': mainWidth + 'px'
				});
				$('g.trajectory.main circle', svgElem).css({
					'stroke-width': mainWidth + 'px'
				}).attr("r", (mainWidth * 3).toString());

			})
		)

		if (this.hysplit) {
			(<MapOverlaySVGContentProducer>this.getProducer()).setSVG(this.hysplit.getLatLngSVG());
		}
	}
	public setHysplit(hysplit: Hysplit): HysplitMapOverlay {
		this.hysplit = hysplit;
		this.updateSVG(undefined);
		return this;
	}
	public updateSVG(tracerMillis: number): void {
		if (this.hysplit) {
			(<MapOverlaySVGContentProducer>this.getProducer()).setSVG(this.hysplit.getLatLngSVG(tracerMillis));
			this.update();
		}
	}
	public refresh(): HysplitMapOverlay {
		this.update();
		return this;
	}
}
